import {Component, computed, inject, OnInit, signal, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {CompanyOfficeFunctionSelectionService} from '../services/company-office-function-selection.service';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {TableModule} from 'primeng/table';
import {ModuleFunction} from '@frontend/directus';
import {FormsModule} from '@angular/forms';
import {Checkbox} from 'primeng/checkbox';
import {ProjectWizardService} from '../services/project-wizard.service';
import {BaseStepComponent} from '../../../../../shared/components/wizard/base-step.component';
import {FunctionSelectionDialogComponent} from '../../../../../shared/components/dialog/function-selection-dialog/function-selection-dialog.component';
import {TooltipModule} from 'primeng/tooltip';
import {ProjectWizardOfficeDataService} from '../services/project-wizard-office-data.service';
import {OfficeViewModel} from './office.models';
import {CreateUpdateProjectRequest} from '@frontend/shared-api';
import {CountryService} from '@frontend/shared';
import {FunctionSelectionDialogStateService} from '../../../../../shared/components/dialog/function-selection-dialog/function-selection-dialog-state.service';
import {FunctionDisplayService} from '../../../../../shared/services/function-display.service';
import {ModuleFunctionDisplayData} from '../../../../../shared/models/module-function-display.model';

@Component({
  selector: 'app-project-wizard-office-step',
  imports: [
    CommonModule,
    TranslocoDirective,
    TableModule,
    FormsModule,
    Checkbox,
    FunctionSelectionDialogComponent,
    TooltipModule,
    TranslocoPipe,
  ],
  providers: [FunctionSelectionDialogStateService],
  templateUrl: './project-wizard-office-step.component.html',
})
export class ProjectWizardOfficeStepComponent
  extends BaseStepComponent<CreateUpdateProjectRequest>
  implements OnInit
{
  readonly functionsDialog = viewChild.required(FunctionSelectionDialogComponent);

  private readonly projectWizardService = inject(ProjectWizardService);
  private readonly companySelectionService = inject(CompanyOfficeFunctionSelectionService);
  private readonly officeDataService = inject(ProjectWizardOfficeDataService);
  private readonly functionDisplayService = inject(FunctionDisplayService);
  private readonly countryService = inject(CountryService);
  private readonly translocoService = inject(TranslocoService);
  protected dialogStateService = inject(FunctionSelectionDialogStateService);

  private readonly activeLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  override readonly form: any;
  override readonly stepIndex = 3;

  readonly dialogTranslationPrefix = 'wizard.project.dialog';

  protected readonly selectedCompanies = this.companySelectionService.selectedCompanies;
  protected readonly selectedCompanyId = signal<number | null>(null);

  protected readonly selectedOfficeIds = computed(() =>
    this.companySelectionService.getSelectedOffices(),
  );
  protected readonly officeViewModels = signal<OfficeViewModel[]>([]);
  protected readonly officesForSelectedCompany = computed(() => {
    const companyId = this.selectedCompanyId();
    if (!companyId) return [];
    return this.officeViewModels().filter((office) => office.companyId === companyId);
  });
  protected readonly countriesForSelectedCompany = computed(() => {
    const countries = this.officeDataService.extractCountriesFromOffices(
      this.officesForSelectedCompany(),
    );
    const currentLang = this.activeLanguage();
    return countries.map((country) => ({
      ...country,
      name: this.countryService.getCountryNameByCode(country.code, currentLang),
    }));
  });

  override ngOnInit() {
    this.selectFirstCompany();
    this.loadInitialOfficeViewModels();
  }

  readonly isValidComputed = computed(() => {
    const selectedOffices = this.selectedOfficeIds();
    if (selectedOffices.size === 0) return false;

    for (const officeId of selectedOffices) {
      const functionsForOffice = this.companySelectionService.getOfficeFunctions(officeId);
      if (functionsForOffice.length === 0) {
        return false;
      }
    }

    return true;
  });

  private loadInitialOfficeViewModels() {
    this.officeViewModels.set(this.officeDataService.flattenCompanyData(this.selectedCompanies()));
  }

  private selectFirstCompany() {
    const companies = this.selectedCompanies();
    if (companies.length > 0) {
      this.selectedCompanyId.set(companies[0].id);
    }
  }

  setSelectedCompany(companyId: number) {
    this.selectedCompanyId.set(companyId);
  }

  getOfficesForCountry(countryId: number): OfficeViewModel[] {
    return this.officesForSelectedCompany().filter((office) => office.countryId === countryId);
  }

  isOfficeSelected(officeId: number): boolean {
    return this.selectedOfficeIds().has(officeId);
  }

  toggleOfficeSelection(officeId: number) {
    const wasSelected = this.selectedOfficeIds().has(officeId);
    this.companySelectionService.toggleOffice(officeId);

    if (wasSelected) {
      this.companySelectionService.clearOfficeFunctions(officeId);
    }

    this.updateStepData();
  }

  private updateStepData(): void {
    const data = this.prepareOfficeModuleFunctionsDataForRequest(
      this.companySelectionService.getAllOfficeFunctions(),
    );
    this.projectWizardService.updateStepData({
      stepIndex: this.stepIndex,
      data: new Set(data),
    });
  }

  private prepareOfficeModuleFunctionsDataForRequest(
    selectedFunctionsMap: Record<number, ModuleFunction[]>,
  ): {
    officeId: number;
    moduleFunctions: Set<string>;
  }[] {
    return Object.entries(selectedFunctionsMap)
      .filter(([, functions]) => functions.length > 0)
      .map(([officeId, functions]) => ({
        officeId: parseInt(officeId, 10),
        moduleFunctions: new Set(functions.map((f) => f.id.toString())),
      }));
  }

  selectFunctions(office: OfficeViewModel) {
    this.dialogStateService.openDialog(office.id, office.name);
    const selectedFunctionsForOffice = this.companySelectionService.getOfficeFunctions(office.id);
    this.resetAndInitializeDialog(selectedFunctionsForOffice);
  }

  resetAndInitializeDialog(selectedFunctionsForOffice: ModuleFunction[]) {
    const functionsDialog = this.functionsDialog();
    if (functionsDialog) {
      functionsDialog.initialSelectedFunctions.set([...selectedFunctionsForOffice]);
      functionsDialog.resetAndInitialize();
    }
  }

  onFunctionsSelected(functions: ModuleFunction[]) {
    const currentOfficeId = this.dialogStateService.currentEntityId();

    if (currentOfficeId !== null) {
      this.companySelectionService.setOfficeFunctions(currentOfficeId, functions);
      this.updateOfficeFunctionsState(currentOfficeId, functions);

      if (functions.length > 0 && !this.selectedOfficeIds().has(currentOfficeId)) {
        this.companySelectionService.toggleOffice(currentOfficeId);
      }

      this.updateStepData();
      this.dialogStateService.closeDialog();
    }
  }

  private updateOfficeFunctionsState(officeId: number, functions: ModuleFunction[]) {
    this.companySelectionService.setOfficeFunctions(officeId, functions);
    this.officeViewModels.update((viewModels) =>
      viewModels.map((vm) =>
        vm.id === officeId ? {...vm, selectedFunctions: [...functions]} : vm,
      ),
    );
  }

  getSelectedFunctionsForDialog(): ModuleFunction[] {
    const officeId = this.dialogStateService.currentEntityId();
    return officeId !== null ? this.companySelectionService.getOfficeFunctions(officeId) : [];
  }

  getOfficeFunctionsDisplayWithTooltipData(office: OfficeViewModel): ModuleFunctionDisplayData[] {
    const functions =
      this.companySelectionService.getOfficeFunctions(office.id) || office.selectedFunctions || [];

    if (functions.length === 0) {
      return [];
    }

    const moduleFunctionMap = this.functionDisplayService.groupFunctionsByModule(functions);
    return this.functionDisplayService.displayAsModuleNameWithFunctionsCount(moduleFunctionMap);
  }
}
